import React, { useState, useMemo, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import {
  CheckCircle2,
  ClipboardEdit,
  ClipboardCheck,
  CalendarClock,
  ArrowRight,
  Kanban,
  Layers,
  Calendar,
  GitBranch,
  BarChart2,
  Users,
  X,
} from 'lucide-react'
import { useSpace, useIssues, useStatuses, useIssueMutations } from '../hooks'
import { IssueModal } from '../components/IssueModal'
import { IssueTypeIcon } from '../components/ListRow'
import { useUserOptions } from '../../../shared/hooks/useUserOptions'
import { useAuth } from '../../../shared/hooks/useAuth'
import { canonicalDepartmentName, isSuperAdminOrOwner, DEPARTMENT_BY_SERVICE_CATEGORY } from '@jira-clone/shared-types'
import { groupServicesByCategory } from '../../../shared/constants/services'
import { api } from '../../../api/client'
import type { Issue } from '../api'
import { cn, cardClassName } from '@jira-clone/ui'

const DAY_MS = 24 * 60 * 60 * 1000

function isWithinLastDays(dateStr?: string | null, days = 7) {
  if (!dateStr) return false
  const time = new Date(dateStr).getTime()
  if (isNaN(time)) return false
  return time >= Date.now() - days * DAY_MS
}

function isWithinNextDays(dateStr?: string | null, days = 7) {
  if (!dateStr) return false
  const time = new Date(dateStr).getTime()
  if (isNaN(time)) return false
  return time >= Date.now() && time <= Date.now() + days * DAY_MS
}

function formatRelativeDaysAgo(dateStr?: string | null) {
  if (!dateStr) return '3d ago'
  const time = new Date(dateStr).getTime()
  if (isNaN(time)) return '3d ago'
  const diffDays = Math.floor((Date.now() - time) / DAY_MS)
  if (diffDays <= 0) return 'today'
  if (diffDays === 1) return '1d ago'
  return `${diffDays}d ago`
}

function isDepartmentMatch(deptA?: string | null, deptB?: string | null): boolean {
  if (!deptA || !deptB) return false
  const a = canonicalDepartmentName(deptA).toLowerCase()
  const b = canonicalDepartmentName(deptB).toLowerCase()
  if (a === b) return true

  // Technology and Development are linked as the same department
  const isDevOrTech = (name: string) =>
    name === 'development' ||
    name === 'technology' ||
    name === 'development department' ||
    name === 'technology department' ||
    name === 'tech' ||
    name === 'software' ||
    name === 'it'
  if (isDevOrTech(a) && isDevOrTech(b)) return true

// Creative and Marketing are one department (older data still says one or the other)
  const isCreativeMarketing = (name: string) =>
    name === 'creative' ||
    name === 'creative department' ||
    name === 'design' ||
    name === 'marketing' ||
    name === 'marketing & growth' ||
    name === 'marketing department' ||
    name === 'creative & marketing'
  if (isCreativeMarketing(a) && isCreativeMarketing(b)) return true

  return false
}

interface DepartmentServicesSectionProps {
  services: string[]
  users: any[]
  issues: Issue[]
  onOpenIssue: (issueId: string) => void
  organizationId?: string
  memberIds?: string[]
}

interface DepartmentTaskModalProps {
  departmentName: string
  category: { category: string; color: string }
  departmentUsers: any[]
  departmentIssues: Issue[]
  onClose: () => void
  onOpenIssue: (issueId: string) => void
}

function DepartmentTaskModal({ departmentName, category, departmentUsers, departmentIssues, onClose, onOpenIssue }: DepartmentTaskModalProps) {
  const deptUserIds = useMemo(() => new Set(departmentUsers.map((u) => u.id)), [departmentUsers])

  const usersWithTasks = useMemo(() => {
    return departmentUsers
      .map((user) => ({
        user,
        tasks: departmentIssues.filter((issue) => {
          if (issue.assignee_id === user.id) return true
          const ids = (issue as any).assignee_ids || (issue as any).assigneeIds
          if (Array.isArray(ids) && ids.includes(user.id)) return true
          return false
        }),
      }))
      .filter((entry) => entry.tasks.length > 0)
      .sort((a, b) => b.tasks.length - a.tasks.length)
  }, [departmentUsers, departmentIssues])

  // Department tasks not assigned to users in the active modal list (e.g. unassigned or legacy assignees)
  const otherOrUnassignedTasks = useMemo(() => {
    const assignedTaskIds = new Set(usersWithTasks.flatMap((u) => u.tasks.map((t) => t.id)))
    return departmentIssues.filter((t) => !assignedTaskIds.has(t.id))
  }, [departmentIssues, usersWithTasks])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: category.color }} />
            <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">{departmentName}</h2>
            <span className="text-xs font-medium text-gray-500 dark:text-slate-400">
              {departmentUsers.length} employee{departmentUsers.length !== 1 ? 's' : ''} · {departmentIssues.length} task{departmentIssues.length !== 1 ? 's' : ''}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(85vh-80px)] p-6">
          {usersWithTasks.length === 0 && otherOrUnassignedTasks.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-8">No active tasks for employees in this department.</p>
          ) : (
            <div className="space-y-6">
              {usersWithTasks.map(({ user, tasks }) => (
                <div key={user.id} className="space-y-2">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: user.color || '#6366f1' }}>
                      {user.initials || (user.name || '?').slice(0, 2).toUpperCase()}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">{user.display_name || user.name}</p>
                      <p className="text-[11px] text-gray-500 dark:text-slate-400">{tasks.length} active task{tasks.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  <div className="ml-10 space-y-1.5">
                    {tasks.map((task) => (
                      <div
                        key={task.id}
                        onClick={() => onOpenIssue(task.id)}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200/80 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 cursor-pointer transition-all group"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <IssueTypeIcon type={task.type} />
                          <span className="rounded bg-[#DEEBFF] px-2 py-0.5 text-xs font-semibold text-[#0747A6] dark:bg-blue-950 dark:text-blue-300 shrink-0">
                            {task.key}
                          </span>
                          <span className="truncate text-sm font-medium text-gray-800 dark:text-slate-200 group-hover:text-blue-600 transition-colors">
                            {task.title}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-3">
                          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${task.status_category === 'done' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' : task.status_category === 'in_progress' ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' : 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300'}`}>
                            {task.status_category.replace('_', ' ')}
                          </span>
                          {task.due_date && (
                            <span className="text-[11px] text-gray-400 dark:text-slate-500">
                              {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {otherOrUnassignedTasks.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-slate-800">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-400 text-xs font-bold text-white">
                      ?
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">Other / Unassigned Department Tasks</p>
                      <p className="text-[11px] text-gray-500 dark:text-slate-400">{otherOrUnassignedTasks.length} task{otherOrUnassignedTasks.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  <div className="ml-10 space-y-1.5">
                    {otherOrUnassignedTasks.map((task) => (
                      <div
                        key={task.id}
                        onClick={() => onOpenIssue(task.id)}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200/80 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 cursor-pointer transition-all group"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <IssueTypeIcon type={task.type} />
                          <span className="rounded bg-[#DEEBFF] px-2 py-0.5 text-xs font-semibold text-[#0747A6] dark:bg-blue-950 dark:text-blue-300 shrink-0">
                            {task.key}
                          </span>
                          <span className="truncate text-sm font-medium text-gray-800 dark:text-slate-200 group-hover:text-blue-600 transition-colors">
                            {task.title}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-3">
                          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${task.status_category === 'done' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' : task.status_category === 'in_progress' ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' : 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300'}`}>
                            {task.status_category.replace('_', ' ')}
                          </span>
                          {task.due_date && (
                            <span className="text-[11px] text-gray-400 dark:text-slate-500">
                              {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function DepartmentServicesSection({ services, users, issues, onOpenIssue, organizationId, memberIds }: DepartmentServicesSectionProps) {
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null)
  const [orgDepartments, setOrgDepartments] = useState<Record<string, string>>({}) // ObjectId -> name

  // Fetch all departments to build ObjectId→name lookup for task matching
  useEffect(() => {
    if (!organizationId) return
    let cancelled = false
    api
      .get<any>('/departments', { params: { organizationId } })
      .then((res: any) => {
        if (cancelled) return
        const map: Record<string, string> = {}
        const rows: any[] = res?.data ?? []
        for (const d of rows) {
          if (d.id || d._id) map[String(d.id || d._id)] = String(d.name)
        }
        setOrgDepartments(map)
      })
      .catch(() => { if (!cancelled) setOrgDepartments({}) })
    return () => { cancelled = true }
  }, [organizationId])

  // Build a lookup map of users: id -> user
  const userMap = useMemo(() => {
    const map = new Map<string, any>()
    for (const u of users) {
      if (u.id) map.set(u.id, u)
    }
    return map
  }, [users])

  // Build a set of project member user IDs for filtering
  const projectMemberIdSet = useMemo(() => new Set(memberIds?.filter(Boolean) ?? []), [memberIds])

  // Map: department display name -> { category, users (in project), issues }
  const grouped = useMemo(() => {
    const serviceGroups = groupServicesByCategory(services)
    return serviceGroups.map(({ category, services: svcs }) => {
// TECHNOLOGY -> "Development", MARKETING & GROWTH and CREATIVE -> "Creative & Marketing"
      const dbDeptName = DEPARTMENT_BY_SERVICE_CATEGORY[category.category] ?? category.departmentLabel

      // Users in this project whose department matches
      const deptUsers = users.filter((u) => {
        if (projectMemberIdSet.size > 0 && !projectMemberIdSet.has(u.id)) return false
        return isDepartmentMatch(u.department, dbDeptName)
      })

      const deptUserIds = new Set(deptUsers.map((u) => u.id))

      // Tasks whose resolved department matches OR whose assignee belongs to this department
      const deptIssues = issues.filter((issue) => {
        // 1. Direct match on issue.department_id
        if (issue.department_id) {
          const deptName = orgDepartments[issue.department_id]
          if (deptName && isDepartmentMatch(deptName, dbDeptName)) return true
        }
        // 2. Match via assignee belonging to this department
        if (issue.assignee_id) {
          if (deptUserIds.has(issue.assignee_id)) return true
          const assignee = userMap.get(issue.assignee_id)
          if (assignee && isDepartmentMatch(assignee.department, dbDeptName)) return true
        }
        // 3. Or check any of the assignees if multiple
        const anyAssigneeMatch = ((issue as any).assignee_ids || []).some((uid: string) => {
          if (deptUserIds.has(uid)) return true
          const a = userMap.get(uid)
          return a && isDepartmentMatch(a.department, dbDeptName)
        })
        if (anyAssigneeMatch) return true

        return false
      })

      return {
        category,
        services: svcs,
        departments: [dbDeptName],
        users: deptUsers,
        issues: deptIssues,
      }
    })
  }, [services, users, issues, orgDepartments, projectMemberIdSet, userMap])

  const selectedGroup = selectedDepartment ? grouped.find((g) => g.departments[0] === selectedDepartment) : null

  return (
    <>
      <div className={cn(cardClassName(), 'mb-4 p-4 space-y-3')}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-800 dark:text-slate-200">Associated Services & Categories:</span>
          <span className="text-xs text-muted-foreground font-medium">({services.length} selected)</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {grouped.map(({ category, services: svcs, departments: deptNames, users: deptUsers, issues: deptIssues }) => (
            <div
              key={category.category}
              onClick={() => setSelectedDepartment(deptNames[0])}
              className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2 cursor-pointer transition-all hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: category.color }} />
                  <span>{category.departmentLabel ?? category.category}</span>
                </div>
                <span className="text-[10px] text-muted-foreground/70 font-normal">({svcs.length})</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {svcs.map((svc) => (
                  <span
                    key={svc}
                    className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium border ${category.badgeClass}`}
                  >
                    {svc}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-3 pt-1.5 border-t border-border/40">
                <div className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-slate-400">
                  <Users className="h-3 w-3" />
                  <span className="font-medium">{deptUsers.length}</span>
                </div>
                <div className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-slate-400">
                  <ClipboardEdit className="h-3 w-3" />
                  <span className="font-medium">{deptIssues.length}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedGroup && (
        <DepartmentTaskModal
          departmentName={selectedGroup.departments[0]}
          category={selectedGroup.category}
          departmentUsers={selectedGroup.users}
          departmentIssues={selectedGroup.issues}
          onClose={() => setSelectedDepartment(null)}
          onOpenIssue={onOpenIssue}
        />
      )}
    </>
  )
}

export function SummaryPage() {
  const navigate = useNavigate()
  const { id: spaceId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedIssueId = searchParams.get('selectedIssue')

  const { user, organization } = useAuth()

  const isSuperAdmin = Boolean(
    (user?.role && isSuperAdminOrOwner(user.role)) ||
    user?.department?.toLowerCase() === 'superadmin',
  )

  const { data: space } = useSpace(spaceId ?? null)
  const { data: rawIssues = [], isLoading } = useIssues(spaceId ?? null, { onlyMine: !isSuperAdmin })
  const { data: statuses = [] } = useStatuses(spaceId ?? null)
  const { data: allUsers = [] } = useUserOptions(null)
  const { update, remove } = useIssueMutations(spaceId ?? null)

  const issues: Issue[] = rawIssues as Issue[]

  // Deep-linked issue
  const selectedIssue = useMemo(() => {
    if (!selectedIssueId) return null
    return (
      issues.find(
        (i) => i.id === selectedIssueId || i.key?.toLowerCase() === selectedIssueId.toLowerCase(),
      ) ?? null
    )
  }, [issues, selectedIssueId])

  const handleOpenIssue = (issueId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('selectedIssue', issueId)
      return next
    })
  }

  const handleCloseIssueModal = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('selectedIssue')
      return next
    })
  }

  // 1. Metric stats
  const stats = useMemo(() => {
    const completed = issues.filter(
      (i) =>
        i.status_category === 'done' ||
        String(i.status).toLowerCase().includes('done'),
    ).length
    const updated = issues.filter((i) => isWithinLastDays(i.updated_at, 7)).length
    const created = issues.filter((i) => isWithinLastDays(i.created_at, 7)).length
    const dueSoon = issues.filter((i) => isWithinNextDays(i.due_date, 7)).length

    return { completed, updated, created, dueSoon }
  }, [issues])

  // 2. Status overview calculation
  const statusStats = useMemo(() => {
    let todo = 0
    let inProgress = 0
    let done = 0

    for (const issue of issues) {
      const cat = issue.status_category || 'todo'
      if (cat === 'done') done++
      else if (cat === 'in_progress') inProgress++
      else todo++
    }

    return { todo, inProgress, done, total: issues.length }
  }, [issues])

  // 3. Priority breakdown calculation
  const priorityStats = useMemo(() => {
    const counts = {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
    }

    for (const issue of issues) {
      const p = (issue.priority || 'medium').toLowerCase()
      if (p in counts) {
        counts[p as keyof typeof counts]++
      } else {
        counts.medium++
      }
    }

    return counts
  }, [issues])

  // 4. Types of work calculation
  const typeStats = useMemo(() => {
    let taskCount = 0
    let subtaskCount = 0

    for (const issue of issues) {
      if (issue.type === 'subtask' || issue.parent_id) {
        subtaskCount++
      } else {
        taskCount++
      }
    }

    const total = issues.length
    const taskPct = total > 0 ? Math.round((taskCount / total) * 100) : 0
    const subtaskPct = total > 0 ? 100 - taskPct : 0

    return { taskCount, subtaskCount, taskPct, subtaskPct }
  }, [issues])

  // 5. Team workload — real per-assignee breakdown, not a fixed fake name.
  const workloadStats = useMemo(() => {
    const total = issues.length
    const countByAssignee = new Map<string, number>()
    let unassignedCount = 0

    for (const issue of issues) {
      if (!issue.assignee_id || issue.assignee_id === 'u-unknown' || issue.assignee_id === 'unassigned') {
        unassignedCount++
      } else {
        countByAssignee.set(issue.assignee_id, (countByAssignee.get(issue.assignee_id) ?? 0) + 1)
      }
    }

    const pct = (count: number) => (total > 0 ? Math.round((count / total) * 100) : 0)
    const userById = new Map(allUsers.map((u) => [u.id, u]))

    const assignees = Array.from(countByAssignee.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([userId, count]) => {
        const user = userById.get(userId)
        return {
          id: userId,
          name: user?.display_name || user?.name || 'Unknown user',
          initials: user?.initials || (user?.name || '?').slice(0, 2).toUpperCase(),
          color: user?.color || '#6554C0',
          count,
          pct: pct(count),
        }
      })

    return {
      unassignedCount,
      unassignedPct: pct(unassignedCount),
      assignees,
    }
  }, [issues, allUsers])

  // 6. Recent activity (top 6 issues)
  const recentActivityIssues = useMemo(() => {
    return [...issues]
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 6)
  }, [issues])

  // Donut chart angles
  const donutMath = useMemo(() => {
    const total = statusStats.total
    const todoPct = total > 0 ? statusStats.todo / total : 0
    const inProgressPct = total > 0 ? statusStats.inProgress / total : 0
    const donePct = total > 0 ? statusStats.done / total : 0

    const circumference = 2 * Math.PI * 50 // r = 50 -> C = 314.159
    const todoStroke = todoPct * circumference
    const inProgressStroke = inProgressPct * circumference
    const doneStroke = donePct * circumference

    return {
      circumference,
      todoStroke,
      inProgressStroke,
      doneStroke,
      inProgressOffset: -todoStroke,
      doneOffset: -(todoStroke + inProgressStroke),
    }
  }, [statusStats])

  return (
    <div className="flex-1 overflow-y-auto p-8 bg-[#FAFBFC] dark:bg-slate-950 font-sans">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Project Services Segregated by Category */}
        {isSuperAdmin && space?.services && space.services.length > 0 && (
          <DepartmentServicesSection
            services={space.services}
            users={allUsers}
            issues={issues}
            onOpenIssue={handleOpenIssue}
            organizationId={organization?.id}
            memberIds={space.members?.map((m) => m.userId)}
          />
        )}

        {/* Metric Cards Row */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {/* Card 1: Completed */}
          <div
            onClick={() => navigate(`/spaces/${spaceId}/list`)}
            className={cn(cardClassName({ interactive: true }), 'flex items-center gap-3.5 p-5 transition-all hover:border-blue-300')}
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400">
              <CheckCircle2 className="h-6 w-6 stroke-[2.2]" />
            </div>
            <div>
              <p className="text-xl font-bold tracking-tight text-gray-900 dark:text-slate-100">
                {stats.completed} completed
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400">in the last 7 days</p>
            </div>
          </div>

          {/* Card 2: Updated */}
          <div
            onClick={() => navigate(`/spaces/${spaceId}/list`)}
            className={cn(cardClassName({ interactive: true }), 'flex items-center gap-3.5 p-5 transition-all hover:border-blue-300')}
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400">
              <ClipboardEdit className="h-6 w-6 stroke-[2.2]" />
            </div>
            <div>
              <p className="text-xl font-bold tracking-tight text-gray-900 dark:text-slate-100">
                {stats.updated} updated
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400">in the last 7 days</p>
            </div>
          </div>

          {/* Card 3: Created */}
          <div
            onClick={() => navigate(`/spaces/${spaceId}/list`)}
            className={cn(cardClassName({ interactive: true }), 'flex items-center gap-3.5 p-5 transition-all hover:border-blue-300')}
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400">
              <ClipboardCheck className="h-6 w-6 stroke-[2.2]" />
            </div>
            <div>
              <p className="text-xl font-bold tracking-tight text-gray-900 dark:text-slate-100">
                {stats.created} created
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400">in the last 7 days</p>
            </div>
          </div>

          {/* Card 4: Due */}
          <div
            onClick={() => navigate(`/spaces/${spaceId}/calendar`)}
            className={cn(cardClassName({ interactive: true }), 'flex items-center gap-3.5 p-5 transition-all hover:border-blue-300')}
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400">
              <CalendarClock className="h-6 w-6 stroke-[2.2]" />
            </div>
            <div>
              <p className="text-xl font-bold tracking-tight text-gray-900 dark:text-slate-100">
                {stats.dueSoon} due
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400">soon in the next 7 days</p>
            </div>
          </div>
        </div>

        {/* Row 1 Charts: Status overview & Priority breakdown */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Status overview (Donut Chart) */}
          <div className={cn(cardClassName(), 'p-6')}>
            <h3 className="text-base font-bold text-gray-900 dark:text-slate-100">Status overview</h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
              A snapshot of the status of your work items.
            </p>

            <div className="mt-6 flex items-center justify-around gap-6">
              {/* SVG Donut */}
              <div className="relative flex items-center justify-center">
                <svg className="h-44 w-44 -rotate-90 transform" viewBox="0 0 120 120">
                  {/* Background Track */}
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="transparent"
                    stroke="#F1F2F4"
                    strokeWidth="16"
                    className="dark:stroke-slate-800"
                  />
                  {/* To Do Slice (#388BFF) */}
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="transparent"
                    stroke="#388BFF"
                    strokeWidth="16"
                    strokeDasharray={`${donutMath.todoStroke} ${donutMath.circumference}`}
                    strokeDashoffset="0"
                    className="cursor-pointer transition-all hover:opacity-90"
                    onClick={() => navigate(`/spaces/${spaceId}/list`)}
                  />
                  {/* In Progress Slice (#8777D9) */}
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="transparent"
                    stroke="#8777D9"
                    strokeWidth="16"
                    strokeDasharray={`${donutMath.inProgressStroke} ${donutMath.circumference}`}
                    strokeDashoffset={donutMath.inProgressOffset}
                    className="cursor-pointer transition-all hover:opacity-90"
                    onClick={() => navigate(`/spaces/${spaceId}/list`)}
                  />
                  {/* Done Slice (#36B37E) */}
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="transparent"
                    stroke="#36B37E"
                    strokeWidth="16"
                    strokeDasharray={`${donutMath.doneStroke} ${donutMath.circumference}`}
                    strokeDashoffset={donutMath.doneOffset}
                    className="cursor-pointer transition-all hover:opacity-90"
                    onClick={() => navigate(`/spaces/${spaceId}/list`)}
                  />
                </svg>
                {/* Center Total Count */}
                <div className="absolute flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-100">
                    {statusStats.total}
                  </span>
                </div>
              </div>

              {/* Legend */}
              <div className="space-y-3">
                <div
                  onClick={() => navigate(`/spaces/${spaceId}/list`)}
                  className="flex items-center gap-2.5 text-xs font-medium text-gray-700 dark:text-slate-300 cursor-pointer hover:text-blue-600 transition-colors"
                >
                  <span className="h-3 w-3 rounded-xs bg-[#388BFF]" />
                  <span>To Do: {statusStats.todo}</span>
                </div>
                <div
                  onClick={() => navigate(`/spaces/${spaceId}/list`)}
                  className="flex items-center gap-2.5 text-xs font-medium text-gray-700 dark:text-slate-300 cursor-pointer hover:text-purple-600 transition-colors"
                >
                  <span className="h-3 w-3 rounded-xs bg-[#8777D9]" />
                  <span>In Progress: {statusStats.inProgress}</span>
                </div>
                <div
                  onClick={() => navigate(`/spaces/${spaceId}/list`)}
                  className="flex items-center gap-2.5 text-xs font-medium text-gray-700 dark:text-slate-300 cursor-pointer hover:text-emerald-600 transition-colors"
                >
                  <span className="h-3 w-3 rounded-xs bg-[#36B37E]" />
                  <span>Done: {statusStats.done}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Priority breakdown (Bar Chart) */}
          <div className={cn(cardClassName(), 'p-6 flex flex-col justify-between')}>
            <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-slate-100">Priority breakdown</h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
                How work is prioritized across this project.
              </p>
            </div>

            {/* Vertical Bar Chart Container */}
            <div className="relative mt-6 h-48 w-full pt-4 pb-6 flex flex-col justify-end">
              {/* Y-axis grid lines */}
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none pb-6">
                {[8, 6, 4, 2, 0].map((val) => (
                  <div key={val} className="flex items-center gap-2 w-full">
                    <span className="w-4 text-[11px] text-gray-400 dark:text-slate-500 text-right">
                      {val}
                    </span>
                    <div className="flex-1 border-b border-dashed border-gray-100 dark:border-slate-800" />
                  </div>
                ))}
              </div>

              {/* Bars Row */}
              <div className="relative z-10 grid grid-cols-4 gap-3 pl-6 pr-2 h-36 items-end">
                {/* Critical */}
                <div
                  onClick={() => navigate(`/spaces/${spaceId}/list`)}
                  className="flex flex-col items-center h-full justify-end group cursor-pointer"
                >
                  <div
                    className="w-full max-w-[36px] bg-[#388BFF] rounded-t-sm transition-all group-hover:brightness-110"
                    style={{ height: `${(priorityStats.critical / 8) * 100}%`, minHeight: priorityStats.critical > 0 ? 4 : 0 }}
                  />
                  <span className="mt-2 text-[11px] font-medium text-gray-500 dark:text-slate-400 group-hover:text-blue-600">
                    Critical
                  </span>
                </div>

                {/* High */}
                <div
                  onClick={() => navigate(`/spaces/${spaceId}/list`)}
                  className="flex flex-col items-center h-full justify-end group cursor-pointer"
                >
                  <div
                    className="w-full max-w-[36px] bg-[#388BFF] rounded-t-sm transition-all group-hover:brightness-110"
                    style={{ height: `${(priorityStats.high / 8) * 100}%` }}
                  />
                  <span className="mt-2 text-[11px] font-medium text-gray-500 dark:text-slate-400 group-hover:text-blue-600">
                    High
                  </span>
                </div>

                {/* Medium */}
                <div
                  onClick={() => navigate(`/spaces/${spaceId}/list`)}
                  className="flex flex-col items-center h-full justify-end group cursor-pointer"
                >
                  <div
                    className="w-full max-w-[36px] bg-[#388BFF] rounded-t-sm transition-all group-hover:brightness-110"
                    style={{ height: `${(priorityStats.medium / 8) * 100}%` }}
                  />
                  <span className="mt-2 text-[11px] font-medium text-gray-500 dark:text-slate-400 group-hover:text-blue-600">
                    Medium
                  </span>
                </div>

                {/* Low */}
                <div
                  onClick={() => navigate(`/spaces/${spaceId}/list`)}
                  className="flex flex-col items-center h-full justify-end group cursor-pointer"
                >
                  <div
                    className="w-full max-w-[36px] bg-[#388BFF] rounded-t-sm transition-all group-hover:brightness-110"
                    style={{ height: `${(priorityStats.low / 8) * 100}%` }}
                  />
                  <span className="mt-2 text-[11px] font-medium text-gray-500 dark:text-slate-400 group-hover:text-blue-600">
                    Low
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Types of work & Team workload */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Types of work */}
          <div className={cn(cardClassName(), 'p-6')}>
            <h3 className="text-base font-bold text-gray-900 dark:text-slate-100">Types of work</h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
              A breakdown of work items by their type.
            </p>

            <div className="mt-5 space-y-3.5">
              {/* Task Row */}
              <div
                onClick={() => navigate(`/spaces/${spaceId}/list`)}
                className="flex items-center gap-3 cursor-pointer group"
              >
                <div className="flex items-center gap-2 min-w-[76px]">
                  <IssueTypeIcon type="task" />
                  <span className="text-xs font-semibold text-gray-800 dark:text-slate-200 group-hover:text-blue-600">
                    Task
                  </span>
                </div>
                <span className="rounded-md bg-[#388BFF] px-2 py-0.5 text-xs font-bold text-white shadow-2xs">
                  {typeStats.taskPct}%
                </span>
                <div className="flex-1 overflow-hidden rounded-md bg-gray-100 dark:bg-slate-800 h-4">
                  <div
                    className="h-full rounded-md bg-[#388BFF] transition-all group-hover:brightness-110"
                    style={{ width: `${typeStats.taskPct}%` }}
                  />
                </div>
              </div>

              {/* Subtask Row */}
              <div
                onClick={() => navigate(`/spaces/${spaceId}/list`)}
                className="flex items-center gap-3 cursor-pointer group"
              >
                <div className="flex items-center gap-2 min-w-[76px]">
                  <IssueTypeIcon type="subtask" />
                  <span className="text-xs font-semibold text-gray-800 dark:text-slate-200 group-hover:text-blue-600">
                    Subtask
                  </span>
                </div>
                <span className="rounded-md bg-[#6B778C] px-2 py-0.5 text-xs font-bold text-white shadow-2xs">
                  {typeStats.subtaskPct}%
                </span>
                <div className="flex-1 overflow-hidden rounded-md bg-gray-100 dark:bg-slate-800 h-4">
                  <div
                    className="h-full rounded-md bg-[#6B778C]/40 transition-all group-hover:brightness-110"
                    style={{ width: `${typeStats.subtaskPct}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Team workload */}
          <div className={cn(cardClassName(), 'p-6')}>
            <h3 className="text-base font-bold text-gray-900 dark:text-slate-100">Team workload</h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
              Monitor the capacity of your team.
            </p>

            <div className="mt-5 space-y-3.5">
              {issues.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-slate-500">No issues yet.</p>
              ) : (
                <>
                  {workloadStats.assignees.map((a) => (
                    <div
                      key={a.id}
                      onClick={() => navigate(`/spaces/${spaceId}/list`)}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <div className="flex items-center gap-2 min-w-[96px]">
                        <span
                          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                          style={{ backgroundColor: a.color }}
                        >
                          {a.initials}
                        </span>
                        <span className="text-xs font-semibold text-gray-800 dark:text-slate-200 truncate group-hover:text-purple-600">
                          {a.name}
                        </span>
                      </div>
                      <span className="rounded-md bg-[#6554C0] px-2 py-0.5 text-xs font-bold text-white shadow-2xs">
                        {a.pct}%
                      </span>
                      <div className="flex-1 overflow-hidden rounded-md bg-gray-100 dark:bg-slate-800 h-4">
                        <div
                          className="h-full rounded-md bg-[#6554C0] transition-all group-hover:brightness-110"
                          style={{ width: `${a.pct}%` }}
                        />
                      </div>
                    </div>
                  ))}

                  {workloadStats.unassignedCount > 0 && (
                    <div
                      onClick={() => navigate(`/spaces/${spaceId}/list`)}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <div className="flex items-center gap-2 min-w-[96px]">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#E3742E] text-[10px] font-bold text-white">
                          U
                        </span>
                        <span className="text-xs font-semibold text-gray-800 dark:text-slate-200 truncate group-hover:text-purple-600">
                          Unassigned
                        </span>
                      </div>
                      <span className="rounded-md bg-[#6554C0] px-2 py-0.5 text-xs font-bold text-white shadow-2xs">
                        {workloadStats.unassignedPct}%
                      </span>
                      <div className="flex-1 overflow-hidden rounded-md bg-gray-100 dark:bg-slate-800 h-4">
                        <div
                          className="h-full rounded-md bg-[#6554C0] transition-all group-hover:brightness-110"
                          style={{ width: `${workloadStats.unassignedPct}%` }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Row 3: Recent activity */}
        <div className={cn(cardClassName(), 'p-6')}>
          <h3 className="text-base font-bold text-gray-900 dark:text-slate-100 mb-4">Recent activity</h3>

          <div className="space-y-1">
            {recentActivityIssues.map((issue) => (
              <div
                key={issue.id}
                onClick={() => handleOpenIssue(issue.id)}
                className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50/80 dark:hover:bg-slate-800/60 cursor-pointer transition-colors group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <IssueTypeIcon type={issue.type} />
                  <span className="rounded bg-[#DEEBFF] px-2 py-0.5 text-xs font-semibold text-[#0747A6] dark:bg-blue-950 dark:text-blue-300">
                    {issue.key}
                  </span>
                  <span className="truncate text-sm font-medium text-gray-800 dark:text-slate-200 group-hover:text-blue-600 group-hover:underline transition-colors">
                    {issue.title}
                  </span>
                </div>
                <span className="shrink-0 text-xs font-medium text-gray-400 dark:text-slate-500 pl-4">
                  {formatRelativeDaysAgo(issue.updated_at)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="py-4 text-center text-xs text-gray-400 dark:text-slate-500">
          {space?.members?.length ?? 0} {space?.members?.length === 1 ? 'person' : 'people'} have access to this project
        </div>
      </div>

      {/* Deep-linked Issue Modal connected to Database */}
      {selectedIssue && (
        <IssueModal
          issue={selectedIssue}
          open={!!selectedIssue}
          onClose={handleCloseIssueModal}
          statuses={statuses}
          members={allUsers}
          epics={(issues as Issue[]).filter((i) => i.type === 'epic')}
          onUpdate={async (id, payload) => {
            await update.mutateAsync({ id, payload })
          }}
          onDelete={async (id) => {
            await remove.mutateAsync(id)
            handleCloseIssueModal()
          }}
        />
      )}
    </div>
  )
}
